// src/services/adService.js
// ============================================================================
// AD SERVICE - Replaces hardcoded MOCK_ADVERTISERS with live ad serving
//
// Usage in screens:
//   import { useAds, AdBanner, AdCardCompact } from '../services/adService';
//
//   const { ads, getNextAd, loading } = useAds('lobby_banner');
//   // Then render <AdBanner ad={getNextAd()} /> in your list
//
// LEGAL COMPLIANCE: Per CUSTODIAL_TERMINOLOGY_GUIDE.md
// - Ads are ad placements purchased by advertisers
// - Advertisers do NOT contribute to prize pools
// - Prize pools are 100% player-funded
// ============================================================================

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { Ionicons } from '@expo/vector-icons';

// ============================================================================
// CONFIGURATION
// ============================================================================

// How often to refresh ads from the server (ms)
const AD_REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

// Show an ad every N items in a list
const AD_EVERY_N_ITEMS = 3;

// Whether to use demo/fallback ads when no live ads are available
const DEMO_FALLBACK_ENABLED = true;

// ============================================================================
// DEMO FALLBACK ADS
// Used when no real advertisers have active campaigns yet.
// These rotate until replaced by real ads from Firestore.
// ============================================================================
const DEMO_ADS = [
  {
    id: 'demo_razer',
    name: 'Razer',
    tagline: 'For Gamers. By Gamers.',
    headline: 'DeathAdder V3 Pro',
    description: 'The #1 Best-Selling Gaming Mouse',
    cta: 'Shop Now',
    url: 'https://www.razer.com',
    color: '#44D62C',
    logoIcon: 'gamepad-variant',
    isDemo: true,
  },
  {
    id: 'demo_gfuel',
    name: 'G FUEL',
    tagline: 'The Official Energy Drink of Esports',
    headline: 'Level Up Your Game',
    description: '40 Delicious Flavors. Zero Sugar.',
    cta: 'Get 20% Off',
    url: 'https://gfuel.com',
    color: '#00FF00',
    logoIcon: 'lightning-bolt',
    isDemo: true,
  },
  {
    id: 'demo_secretlab',
    name: 'Secretlab',
    tagline: 'Award-Winning Gaming Chairs',
    headline: 'TITAN Evo 2024',
    description: 'Engineered for marathon gaming sessions',
    cta: 'Configure Yours',
    url: 'https://secretlab.co',
    color: '#E91E63',
    logoIcon: 'seat',
    isDemo: true,
  },
  {
    id: 'demo_discord',
    name: 'Discord Nitro',
    tagline: 'Your place to talk and hang out',
    headline: 'Unlock the full Discord experience',
    description: 'HD streaming, custom emojis, bigger uploads',
    cta: 'Try Nitro',
    url: 'https://discord.com/nitro',
    color: '#5865F2',
    logoIcon: 'forum',
    isDemo: true,
  },
  {
    id: 'demo_steelseries',
    name: 'SteelSeries',
    tagline: 'Glory Awaits',
    headline: 'Arctis Nova Pro',
    description: 'The pinnacle of gaming audio',
    cta: 'Learn More',
    url: 'https://steelseries.com',
    color: '#FF5500',
    logoIcon: 'headphones',
    isDemo: true,
  },
  {
    id: 'demo_hyperx',
    name: 'HyperX',
    tagline: 'We Are All Gamers',
    headline: 'Cloud III Wireless',
    description: '120-hour battery life gaming headset',
    cta: 'Shop HyperX',
    url: 'https://hyperx.com',
    color: '#E4002B',
    logoIcon: 'headset',
    isDemo: true,
  },
];

// ============================================================================
// AD SERVICE SINGLETON
// Manages ad fetching, caching, rotation, and tracking.
// ============================================================================

class AdServiceManager {
  constructor() {
    this._cache = {};            // { placement: { ads: [], fetchedAt: timestamp } }
    this._rotationIndex = {};    // { placement: number }
    this._functions = null;
    this._trackQueue = [];       // Batch impression/click tracking
    this._trackTimer = null;
  }

  _getFunctions() {
    if (!this._functions) this._functions = getFunctions();
    return this._functions;
  }

  /**
   * Fetch ads for a placement from the server.
   * Falls back to demo ads if the fetch fails or returns empty.
   */
  async fetchAds(placement, game = null) {
    const cacheKey = `${placement}_${game || 'all'}`;
    const cached = this._cache[cacheKey];

    // Return cached if fresh
    if (cached && (Date.now() - cached.fetchedAt) < AD_REFRESH_INTERVAL) {
      return cached.ads;
    }

    try {
      const getActiveAds = httpsCallable(this._getFunctions(), 'getActiveAds');
      const result = await getActiveAds({ placement, game, limit: 20 });
      const ads = result.data?.ads || [];

      if (ads.length > 0) {
        this._cache[cacheKey] = { ads, fetchedAt: Date.now() };
        return ads;
      }
    } catch (error) {
      console.log('[AdService] Failed to fetch ads, using fallback:', error.message);
    }

    // Fallback to demo ads
    if (DEMO_FALLBACK_ENABLED) {
      return DEMO_ADS;
    }

    return [];
  }

  /**
   * Get the next ad in rotation for a placement.
   */
  getNextAd(placement, ads) {
    if (!ads || ads.length === 0) return null;

    if (!this._rotationIndex[placement]) this._rotationIndex[placement] = 0;
    const idx = this._rotationIndex[placement] % ads.length;
    this._rotationIndex[placement]++;

    return ads[idx];
  }

  /**
   * Reset rotation index for a placement.
   */
  resetRotation(placement) {
    this._rotationIndex[placement] = 0;
  }

  /**
   * Track an impression. Batched and sent periodically.
   */
  trackImpression(creativeId, placement, screen) {
    if (!creativeId || creativeId.startsWith('demo_')) return; // Don't track demo ads

    this._trackQueue.push({
      type: 'impression',
      creativeId,
      placement,
      screen,
      timestamp: Date.now(),
    });

    this._scheduleFlush();
  }

  /**
   * Track a click. Sent immediately.
   */
  async trackClick(creativeId, placement, screen) {
    if (!creativeId || creativeId.startsWith('demo_')) return;

    try {
      const trackAdClick = httpsCallable(this._getFunctions(), 'trackAdClick');
      await trackAdClick({ creativeId, placement, screen });
    } catch (error) {
      console.log('[AdService] Click tracking failed:', error.message);
    }
  }

  /**
   * Flush queued impressions to the server.
   */
  _scheduleFlush() {
    if (this._trackTimer) return;
    this._trackTimer = setTimeout(() => this._flush(), 5000); // Flush every 5s
  }

  async _flush() {
    this._trackTimer = null;
    const queue = [...this._trackQueue];
    this._trackQueue = [];

    if (queue.length === 0) return;

    // Group by creativeId and send batch
    const grouped = {};
    queue.forEach(item => {
      if (!grouped[item.creativeId]) grouped[item.creativeId] = [];
      grouped[item.creativeId].push(item);
    });

    try {
      const trackAdImpression = httpsCallable(this._getFunctions(), 'trackAdImpression');
      // Send one call per creative (keep it simple for now)
      await Promise.all(
        Object.entries(grouped).map(([creativeId, items]) =>
          trackAdImpression({
            creativeId,
            placement: items[0].placement,
            screen: items[0].screen,
          })
        )
      );
    } catch (error) {
      console.log('[AdService] Impression tracking flush failed:', error.message);
    }
  }

  /**
   * Clear all caches (e.g. on logout).
   */
  clearCache() {
    this._cache = {};
    this._rotationIndex = {};
  }
}

// Singleton instance
const adService = new AdServiceManager();
export { adService };

// ============================================================================
// REACT HOOK: useAds
// ============================================================================

/**
 * Hook to fetch and manage ads for a given placement.
 *
 * @param {string} placement - One of the PLACEMENT_TYPES values
 * @param {string} [game] - Optional game filter
 * @returns {{ ads: Array, getNextAd: Function, loading: boolean, refreshAds: Function }}
 */
export function useAds(placement, game = null) {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    loadAds();

    return () => {
      mountedRef.current = false;
      adService.resetRotation(placement);
    };
  }, [placement, game]);

  const loadAds = async () => {
    setLoading(true);
    const fetched = await adService.fetchAds(placement, game);
    if (mountedRef.current) {
      setAds(fetched);
      setLoading(false);
    }
  };

  const getNextAd = useCallback(() => {
    return adService.getNextAd(placement, ads);
  }, [placement, ads]);

  const refreshAds = useCallback(async () => {
    // Clear cache for this placement to force refresh
    adService._cache[`${placement}_${game || 'all'}`] = null;
    await loadAds();
  }, [placement, game]);

  return { ads, getNextAd, loading, refreshAds };
}

// ============================================================================
// HELPER: Build list with ads inserted
// ============================================================================

/**
 * Insert ad markers into an event list at intervals.
 *
 * @param {Array} items - The event items
 * @param {number} [everyN] - Insert an ad every N items
 * @returns {Array} - Mixed array of { type: 'event', data } and { type: 'ad', key }
 */
export function insertAdsIntoList(items, everyN = AD_EVERY_N_ITEMS) {
  const result = [];
  items.forEach((item, index) => {
    result.push({ type: 'event', data: item, key: item.id || `event-${index}` });
    if ((index + 1) % everyN === 0 && index < items.length - 1) {
      result.push({ type: 'ad', key: `ad-${index}` });
    }
  });
  return result;
}

// ============================================================================
// AD COMPONENTS (drop-in replacements for the mock components)
// ============================================================================

/**
 * Full-width ad banner (used in Lobby and Live Matches).
 * Drop-in replacement for AdBannerLobby.
 */
export function AdBanner({ ad, placement, screen, onAdPress }) {
  if (!ad) return null;

  const handlePress = () => {
    // Track click
    adService.trackClick(ad.id, placement || 'unknown', screen || 'unknown');
    // Open URL
    if (ad.url && typeof window !== 'undefined') {
      window.open(ad.url, '_blank');
    }
    if (onAdPress) onAdPress(ad);
  };

  // Track impression on render
  useEffect(() => {
    adService.trackImpression(ad.id, placement || 'unknown', screen || 'unknown');
  }, [ad.id]);

  return (
    <Pressable style={[bannerStyles.container, { borderColor: (ad.color || '#FFF') + '30' }]} onPress={handlePress}>
      <View style={[bannerStyles.accent, { backgroundColor: ad.color || '#FFF' }]} />
      <View style={bannerStyles.content}>
        {/* Left: Logo + Brand */}
        <View style={bannerStyles.left}>
          <View style={[bannerStyles.logoWrap, { backgroundColor: (ad.color || '#FFF') + '20' }]}>
            <MaterialCommunityIcons name={ad.logoIcon || 'tag'} size={24} color={ad.color || '#FFF'} />
          </View>
          <View style={bannerStyles.brandInfo}>
            <Text style={[bannerStyles.brand, { color: ad.color || '#FFF' }]}>{ad.name || 'Ad'}</Text>
            <Text style={bannerStyles.tagline} numberOfLines={1}>{ad.tagline || ''}</Text>
          </View>
        </View>

        {/* Center: Headline + Description */}
        <View style={bannerStyles.center}>
          <Text style={bannerStyles.headline}>{ad.headline || ''}</Text>
          <Text style={bannerStyles.description} numberOfLines={1}>{ad.description || ''}</Text>
        </View>

        {/* Right: CTA */}
        <View style={bannerStyles.right}>
          <View style={[bannerStyles.ctaBtn, { backgroundColor: ad.color || '#FFF' }]}>
            <Text style={bannerStyles.ctaText}>{ad.cta || 'Learn More'}</Text>
            <Ionicons name="arrow-forward" size={12} color="#000" style={{ marginLeft: 4 }} />
          </View>
          <Text style={bannerStyles.sponsoredLabel}>Sponsored</Text>
        </View>
      </View>
    </Pressable>
  );
}

/**
 * Compact inline ad card (used between event listings).
 * Drop-in replacement for AdCardCompact.
 */
export function AdCardCompact({ ad, placement, screen, onAdPress }) {
  if (!ad) return null;

  const handlePress = () => {
    adService.trackClick(ad.id, placement || 'unknown', screen || 'unknown');
    if (ad.url && typeof window !== 'undefined') {
      window.open(ad.url, '_blank');
    }
    if (onAdPress) onAdPress(ad);
  };

  useEffect(() => {
    adService.trackImpression(ad.id, placement || 'unknown', screen || 'unknown');
  }, [ad.id]);

  return (
    <Pressable style={[compactStyles.container, { borderColor: (ad.color || '#FFF') + '30' }]} onPress={handlePress}>
      <View style={[compactStyles.accent, { backgroundColor: ad.color || '#FFF' }]} />
      <View style={compactStyles.content}>
        <View style={[compactStyles.logoSmall, { backgroundColor: (ad.color || '#FFF') + '20' }]}>
          <MaterialCommunityIcons name={ad.logoIcon || 'tag'} size={20} color={ad.color || '#FFF'} />
        </View>

        <View style={compactStyles.info}>
          <View style={compactStyles.header}>
            <Text style={[compactStyles.brand, { color: ad.color || '#FFF' }]}>{ad.name || 'Ad'}</Text>
            <Text style={compactStyles.sponsored}>Ad</Text>
          </View>
          <Text style={compactStyles.headline} numberOfLines={1}>{ad.headline || ''}</Text>
          <Text style={compactStyles.desc} numberOfLines={1}>{ad.description || ''}</Text>
        </View>

        <View style={[compactStyles.cta, { backgroundColor: ad.color || '#FFF' }]}>
          <Text style={compactStyles.ctaText}>{ad.cta || 'View'}</Text>
        </View>
      </View>
    </Pressable>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const bannerStyles = StyleSheet.create({
  container: {
    backgroundColor: '#0d0d0d',
    borderRadius: 10,
    marginHorizontal: 12,
    marginVertical: 6,
    borderWidth: 1,
    overflow: 'hidden',
  },
  accent: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 3,
    borderTopLeftRadius: 10,
    borderBottomLeftRadius: 10,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 14,
    paddingLeft: 14,
    gap: 12,
  },
  left: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  logoWrap: {
    width: 40,
    height: 40,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  brandInfo: {
    maxWidth: 100,
  },
  brand: {
    fontSize: 13,
    fontWeight: 'bold',
  },
  tagline: {
    fontSize: 10,
    color: '#666',
    marginTop: 1,
  },
  center: {
    flex: 1,
  },
  headline: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#FFF',
  },
  description: {
    fontSize: 11,
    color: '#888',
    marginTop: 2,
  },
  right: {
    alignItems: 'center',
  },
  ctaBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  ctaText: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#000',
  },
  sponsoredLabel: {
    fontSize: 9,
    color: '#555',
    marginTop: 4,
  },
});

const compactStyles = StyleSheet.create({
  container: {
    backgroundColor: '#0d0d0d',
    borderRadius: 8,
    marginHorizontal: 12,
    marginVertical: 4,
    borderWidth: 1,
    overflow: 'hidden',
  },
  accent: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 2,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    paddingLeft: 12,
    gap: 10,
  },
  logoSmall: {
    width: 32,
    height: 32,
    borderRadius: 6,
    justifyContent: 'center',
    alignItems: 'center',
  },
  info: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  brand: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  sponsored: {
    fontSize: 9,
    color: '#555',
    backgroundColor: '#1A1A1A',
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderRadius: 3,
  },
  headline: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFF',
    marginTop: 1,
  },
  desc: {
    fontSize: 10,
    color: '#888',
  },
  cta: {
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 5,
  },
  ctaText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#000',
  },
});